import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import Landing from './pages/Landing';
import AppLayout from './layouts/AppLayout';
import Home from './pages/Home';
import Session from './pages/Session';
import Subscription from './pages/Subscription';
import Account from './pages/Account';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import History from './pages/History';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import NotFound from './pages/NotFound';
import StripeTest from './pages/StripeTest';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { SessionProvider } from './contexts/SessionContext';
import { CreditProvider } from './contexts/CreditContext';
import { APIKeyProvider } from './contexts/APIKeyContext';

function App() {
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();
    if (loading) return <div>Loading...</div>;
    return user ? <>{children}</> : <Navigate to="/login" replace />;
  };

  return (
    <Router>
      <Helmet>
        <link rel="icon" href="/favicon.png" type="image/png" />
        <meta property="og:image" content="/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <APIKeyProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <CreditProvider>
              <SessionProvider>
                <AnimatePresence mode="wait">
                  <Routes>
                    {/* Protected app routes */}
                    <Route element={<AppLayout />}>
                      <Route path="/app" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                      <Route path="/session" element={<ProtectedRoute><Session /></ProtectedRoute>} />
                      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                    </Route>

                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/subscription" element={<Subscription />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/stripe-test" element={<StripeTest />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />

                    {/* Catch all */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AnimatePresence>
              </SessionProvider>
            </CreditProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </APIKeyProvider>
    </Router>
  );
}

export default App;